import { ChakraFabButton } from 'app/components/Core';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { useContextMenu } from 'app/components/ChakraTableList';
import {
  getColumns,
  getNewSelectedRowsIds,
  getSongListMenuOptions,
} from 'app/components/Song/SongList/utils';
import { FolderItem, Song } from 'types/song';
import { LeaveSongDialog } from 'app/components/Song/Collaborators/LeaveSongDialog';
import { useCallback, useMemo, useState } from 'react';
import { useClickedOutsideElement } from 'app/utils/hooks';
import { useAppSelector } from 'app/store';
import { getPinnedSongs } from 'app/store/SongList/selectors';
import { SongRow } from 'app/components/Song/SongList/SongRow';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { SongListComponentProps } from './types';
import { RemoveFromFolderDialog } from './RemoveFromFolderDialog';
import { SongEmptyList } from './SongEmptyList';

export const SongListComponent = ({
  folder,
  canCreateSong,
  isCreateSongLoading,
  loading,
  onNewSong,
  songs,
  infiniteScrollProps,
  loadingMore,
  sortParams,
  onRowClick,
  audioGuid,
  isPlaying,
  handlePlay,
  isAudioLoading,
  dispatch,
}: SongListComponentProps) => {
  const [leaveSong, setLeaveSong] = useState<Song>();
  const [showRemoveFromFolderDialog, setShowRemoveFromFolderDialog] = useState<{
    folder: FolderItem;
    song: Song;
    selectedRowsIds: string[];
  }>();
  const [selectedRowsIds, setSelectedRowsIds] = useState<string[]>([]);
  const pinnedSongs = useAppSelector(getPinnedSongs);

  const onRowSelect = useCallback(
    ({ id }: { id: string }, shift = false) => {
      const newSelectedRowsIds = getNewSelectedRowsIds({
        songGuid: id,
        shift,
        selectedRowsIds,
        songs,
      });

      setSelectedRowsIds(newSelectedRowsIds);
    },
    [selectedRowsIds, songs, setSelectedRowsIds]
  );

  const { ref } = useClickedOutsideElement<HTMLDivElement>(() => {
    if (selectedRowsIds.length) {
      setSelectedRowsIds([]);
    }
  });

  const contextMenuProps = useContextMenu();
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(
    () => getColumns({ loading, sortParams, totalWidth }),
    [loading, sortParams, totalWidth]
  );

  const tableKey = 'songs';

  const rowRenderer = useCallback(
    (row: Row<Song>) => {
      const song = row.original;
      const { guid: songGuid } = song;
      const isPinned = pinnedSongs.some(({ guid }) => songGuid === guid);
      const options = getSongListMenuOptions(
        song,
        setLeaveSong,
        isPinned,
        dispatch,
        folder,
        selectedRowsIds,
        setShowRemoveFromFolderDialog
      );
      return (
        <SongRow
          key={row.id}
          song={song}
          options={options}
          isPlaying={isPlaying}
          handlePlay={handlePlay}
          isAudioLoading={isAudioLoading}
          audioGuid={audioGuid}
          selectedRowsIds={selectedRowsIds}
          onRowSelect={onRowSelect}
          onNavigated={onRowClick}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [
      audioGuid,
      contextMenuProps,
      dispatch,
      folder,
      handlePlay,
      isAudioLoading,
      isPlaying,
      onRowClick,
      onRowSelect,
      pinnedSongs,
      selectedRowsIds,
    ]
  );

  return (
    <>
      <ChakraFabButton
        isDisabled={!canCreateSong || isCreateSongLoading}
        aria-label="add"
        data-testid="add-song"
        onClick={onNewSong}
        leftIcon={<AddIcon width={16} height={16} />}
      >
        New Song
      </ChakraFabButton>

      {!loading && songs.length === 0 ? (
        <SongEmptyList folder={folder} />
      ) : (
        <StickyTable<Song>
          tableKey={tableKey}
          ref={ref}
          data={songs}
          columns={columns}
          infiniteScrollProps={infiniteScrollProps}
          loadingMore={loadingMore}
          rowRenderer={rowRenderer}
          contextMenuProps={contextMenuProps}
          loading={loading}
        />
      )}
      <LeaveSongDialog
        dispatch={dispatch}
        context={folder ? 'folderSongList' : 'songList'}
        song={leaveSong}
        open={!!leaveSong}
        onClose={() => setLeaveSong(undefined)}
      />
      <RemoveFromFolderDialog
        dispatch={dispatch}
        {...showRemoveFromFolderDialog}
        open={!!showRemoveFromFolderDialog}
        onClose={() => setShowRemoveFromFolderDialog(undefined)}
      />
    </>
  );
};
